/** Your top person: you and them, pulled together on a line the replies run along, then your top three. */

import { gsap } from "gsap";
import type { CSSProperties } from "react";

import { Avatar, Fit, H, Letters, Scene, W, Zone, useStageHeight, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { wr } from "../../theme";
import type { You } from "../../types";
import { count } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "blurple", x: 1040, y: 1860, r: 240, seed: 3.1, wob: 0.18 },
  { colour: "yellow", x: 60, y: 1080, r: 100, seed: 4.4, wob: 0.24 },
];

const SIZE = 280;
// Where the two avatars settle, and where they pop up before they pull together.
const A = { x: 340, y: 720 };
const B = { x: 740, y: 720 };
const A_START = { x: 190, y: 560 };
const B_START = { x: 890, y: 920 };
const DOTS = 4;
// As long as the deck shows the card, so the dots keep running until it moves on.
const LENGTH = 6.5;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** How far along the line dot `i` is at time `t`: two run one way, two the other. */
function dotAt(i: number, t: number) {
  const u = (((t * 0.5 + i / DOTS) % 1) + 1) % 1;
  return 0.28 + (i % 2 ? 1 - u : u) * 0.44;
}

const CLIP: CSSProperties = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

export function TopPeople({ people, name }: { people: You["top_people"]; name: string }) {
  const [first] = people;
  // A list of one would only repeat the big name.
  const top = people.length > 1 ? people.slice(0, 3) : [];
  const tall = useStageHeight();

  const root = useTimeline((s) => {
    const [avA, avB] = s.q(".p-av");
    const [line] = s.q(".p-line");
    const dots = s.q(".p-dot");

    s.rise(".p-kick, .lead", 0.35, { y: 30, stagger: 0.12 });
    s.pop(avA, 0.6, { from: 0, ease: "back.out(2.2)" });
    s.pop(avB, 0.75, { from: 0, ease: "back.out(2.2)", sound: false });
    s.tl.fromTo(avA, { x: A_START.x - A.x, y: A_START.y - A.y }, { x: 0, y: 0, duration: 1.2, ease: "elastic.out(1, 0.5)" }, 1.45);
    s.tl.fromTo(avB, { x: B_START.x - B.x, y: B_START.y - B.y }, { x: 0, y: 0, duration: 1.2, ease: "elastic.out(1, 0.5)" }, 1.45);
    s.sound("whoosh", 1.45);

    // The line reaches from you to them, and the dots fade in once you're together.
    const reach = { p: 0 };
    const glow = { o: 0 };
    s.tl.to(reach, { p: 1, duration: 0.5, ease: "power2.out" }, 1.0);
    s.tl.to(glow, { o: 1, duration: 0.3, ease: "none" }, 2.1);
    const paint = () => {
      const t = s.tl.time();
      const ax = A.x + Number(gsap.getProperty(avA, "x"));
      const ay = A.y + Number(gsap.getProperty(avA, "y"));
      const bx = B.x + Number(gsap.getProperty(avB, "x"));
      const by = B.y + Number(gsap.getProperty(avB, "y"));
      line.setAttribute("x1", String(ax));
      line.setAttribute("y1", String(ay));
      line.setAttribute("x2", String(lerp(ax, bx, reach.p)));
      line.setAttribute("y2", String(lerp(ay, by, reach.p)));
      line.setAttribute("opacity", reach.p > 0.001 ? "1" : "0");
      dots.forEach((dot, i) => {
        const k = dotAt(i, t);
        dot.setAttribute("cx", String(lerp(ax, bx, k)));
        dot.setAttribute("cy", String(lerp(ay, by, k)));
        dot.setAttribute("opacity", String(glow.o));
      });
    };
    paint();
    // Painted after the timeline's tweens each frame, so it reads where the avatars are now.
    s.tl.eventCallback("onUpdate", paint);
    // Nothing else runs this long; it keeps the dots moving until the card moves on.
    s.tl.to({}, { duration: LENGTH }, 0);

    s.rise(".p-name", 2.1, { y: 20, stagger: 0.06, sound: false });
    s.letters(".p-big", 2.35, { y: 100 });
    s.rise(".p-replies", 2.9, { y: 24 });
    if (top.length) {
      s.rise(".p-top", 3.3, { y: 0, sound: false });
      s.rise(".t3", 3.4, { y: 30, stagger: 0.12 });
    }
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div className="kick p-kick">Your top person</div>
          <div className="lead">The one you reply to the most</div>
        </Zone>
        {/* You, them and the line between: the moment, so it stays centred. */}
        <Zone at="middle">
          <svg className="ov" viewBox={`0 0 ${W} ${H}`} aria-hidden>
            <line
              className="p-line"
              x1={A.x}
              y1={A.y}
              x2={B.x}
              y2={B.y}
              style={{ stroke: wr("ink"), strokeWidth: 16, strokeLinecap: "round" }}
            />
            {Array.from({ length: DOTS }, (_, i) => (
              <circle key={i} className="p-dot" r={13} cx={lerp(A.x, B.x, dotAt(i, LENGTH))} cy={A.y} style={{ fill: wr("white") }} />
            ))}
          </svg>
          <Avatar name={name} size={SIZE} className="p-av" style={{ left: A.x - SIZE / 2, top: A.y - SIZE / 2 }} />
          <Avatar name={first.name} size={SIZE} className="p-av" style={{ left: B.x - SIZE / 2, top: B.y - SIZE / 2 }} />
          <div className="abs mono p-name" style={{ left: A.x - 140, top: 880, width: 280, textAlign: "center", fontSize: 32, fontWeight: 700 }}>
            you
          </div>
          <Fit size={32} min={18} className="abs mono p-name" style={{ left: B.x - 200, top: 880, width: 400, textAlign: "center", fontWeight: 700 }}>
            {first.name}
          </Fit>
          {/* Pinned by its foot, so a long name that shrinks still sits on the line below. The zone is as tall as the stage. */}
          <Fit size={250} min={60} className="abs disp p-big" style={{ left: 62, right: 62, bottom: tall - 1185 }}>
            <Letters text={first.name} />
          </Fit>
          <div className="line p-replies" style={{ top: 1210 }}>
            You replied to them <b>{count(first.replies)}</b> {first.replies === 1 ? "time" : "times"}.
          </div>
        </Zone>
        {top.length ? (
          <Zone at="bottom">
            <div className="kick p-top" style={{ top: 1340 }}>
              Your top {top.length}
            </div>
            {top.map((p, i) => (
              <div key={p.name} className="t3" style={{ top: 1390 + i * 92 }}>
                <span className="rk">#{i + 1}</span>
                <Avatar name={p.name} inline />
                <b style={CLIP}>{p.name}</b>
                <em>{count(p.replies)}</em>
              </div>
            ))}
          </Zone>
        ) : null}
      </Scene>
    </div>
  );
}
